import React, { createContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  TableSortLabel,
  tableCellClasses,
  styled
} from '@mui/material';
import { MdDownload, MdUpload } from 'react-icons/md';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getYear, getDate, getMonth, parseISO, format } from 'date-fns';
import { toast } from 'react-toastify';
import Toast from '../../../../components/Toast';
import { options } from '../../../../helpers/toastOptions';
import { Header } from '../../../../components/Header';
import { ModalLoading } from '../../../../components/ModalLoading';
import { useAuth } from '../../../../hooks/Auth';
import { useModal } from '../../../../hooks/Modal';
import { useTracking } from '../../../../hooks/Tracking';
import { ModalDownloadDocuments } from './components/ModalDownloadDocuments';
import { ModalDownloadInvoices } from './components/ModalDownloadInvoices';
import { ModalUploadDocuments } from './components/ModalUploadDocuments';
import { ModalUploadInvoices } from './components/ModalUploadInvoices';
import { ModalPrintTag } from './components/ModalPrintTag';
import {
  Container,
  Content,
  Route,
  Title,
  ContentContainer,
  TextInLine,
  Legend,
  IconButton
} from './styled';

export const MyCollectionsContext = createContext({});

const MyCollections = () => {
  const [pendingPickupList, setPendingPickupList] = useState([]);
  const [protocolToModal, setProtocolToModal] = useState('');
  const [branchParameterizationToModal, setBranchParameterizationToModal] =
    useState('');
  const [pickupIdToModal, setPickupIdToModal] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [documentListToDownload, setDocumentListToDownload] = useState([]);
  const [invoiceListToDownload, setInvoiceListToDownload] = useState([]);
  const [limitToUploadInvoice, setLimitToUploadInvoice] = useState(0);
  const [isPickupWithDeclaration, setIsPickupWithDeclaration] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [currentPageState, setCurrentPageState] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isSubmittingPagination, setIsSubmittingPagination] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const { user, signInInternal } = useAuth();
  const { openModal, closeModal, openedModal } = useModal();
  const { getPendingPickups, findPickupById, getPendingPickupsFilter } =
    useTracking();

  const maxXS = useMediaQuery('(max-width:480px)');
  const maxSM = useMediaQuery('(max-width:768px)');
  const maxMD = useMediaQuery('(max-width:900px)');

  const history = useHistory();

  const handleClickSearchButton = () => {
    history.push('/area-do-cliente/minha-conta/search-minhas-coletas');
  };

  const openDownloadDocumentsModal = () => {
    openModal('modalDownloadDocuments');
  };

  const openDownloadInvoicesModal = () => {
    openModal('modalDownloadInvoices');
  };

  const openUploadDocumentsModal = () => {
    openModal('modalUploadDocuments');
  };

  const openUploadInvoicesModal = () => {
    openModal('modalUploadInvoices');
  };

  const openPrintTagModal = () => {
    openModal('modalPrintTag');
  };

  const openLoadingModal = () => {
    openModal('modalLoading');
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage + 1);

    setIsSubmittingPagination(true);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);

    setIsSubmittingPagination(true);
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    },
    '&:last-child td, &:last-child th': {
      border: 0
    }
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const onComponentDidMount = async () => {
    const { search } = window.location;
    const legacyToken = new URLSearchParams(search).get('token');
    const localLegacyToken = localStorage.getItem('@Braspress:legacyToken');

    if (!user || (legacyToken && legacyToken !== localLegacyToken)) {
      openModal('modalLoading');

      if (legacyToken) {
        const signInResponse = await signInInternal({ legacyToken });

        if (signInResponse === 'Invalid token') {
          window.location.href = `${process.env.REACT_APP_URL_CLIENT_VIEW}${process.env.REACT_APP_URL_BOTAO_VOLTAR}`;
        }
      } else {
        window.location.href = `${process.env.REACT_APP_URL_CLIENT_VIEW}${process.env.REACT_APP_URL_BOTAO_VOLTAR}`;
      }

      return;
    }

    setPendingPickupList([]);
    // setPage(1);
    openLoadingModal();

    // eslint-disable-next-line prefer-const
    let params = {};

    if (isSubmittingPagination === true) {
      params = {
        currentPage: page,
        pageSize: rowsPerPage,
        totalPages,
        totalRecords
      };
    } else {
      params = {
        currentPage: 1,
        pageSize: 10
      };
    }

    try {
      setRows([]);
      setPendingPickupList([]);
      setIsSearching(true);

      const pendingPickups = await getPendingPickupsFilter(params);

      if (pendingPickups) {
        const { records, currentPage } = pendingPickups;

        if (records?.length > 0) {
          const list = [];

          const newPickupPendingList = [];

          records.forEach((pickup) => {
            newPickupPendingList.push({ pickup: {} });
            const { createdAt, transportTypeId } = pickup;

            const day = String(getDate(parseISO(createdAt))).padStart(2, '0');
            const month = String(getMonth(parseISO(createdAt)) + 1).padStart(
              2,
              '0'
            );
            const year = getYear(parseISO(createdAt));

            const newCreationDate = `${day}/${month}/${year}`;

            const modal = transportTypeId === 1 ? 'Rodoviário' : 'Rodoaéreo';

            list.push({
              modal,
              formattedCreatedAt: newCreationDate,
              ...pickup
            });
          });

          setPage(currentPage);
          setTotalRecords(pendingPickups.totalRecords);
          setTotalPages(pendingPickups.totalPages);

          setRows(list);
          setPendingPickupList(newPickupPendingList);
        } else {
          setIsSearching(false);
          handleClickSearchButton();
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      closeModal();
    }
  };

  useEffect(() => {
    onComponentDidMount();
  }, [user, totalRecords, totalPages, page]);

  const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px'
  };

  const contextValues = {
    protocolToModal,
    setProtocolToModal,
    branchParameterizationToModal,
    setBranchParameterizationToModal,
    pickupIdToModal,
    setPickupIdToModal,
    modalMessage,
    setModalMessage,
    documentListToDownload,
    setDocumentListToDownload,
    invoiceListToDownload,
    setInvoiceListToDownload,
    limitToUploadInvoice,
    isPickupWithDeclaration,
    setIsPickupWithDeclaration,
    refreshSearch: onComponentDidMount
  };

  return (
    <MyCollectionsContext.Provider value={contextValues}>
      <Container>
        {/* <Header /> */}
        <Content>
          <Box style={{ margin: '0 40px', paddingBottom: '40px' }}>
            {/* <Route>
              {!maxSM && (
                <>
                  Home {'>'} Área do Cliente {'>'} Minha Conta {'>'} Minhas
                  Coletas {'>'} Quadro Solicitações Pendentes
                </>
              )}

              {!!user && (
                <Stack
                  direction="row"
                  spacing={2}
                  style={{
                    alignItems: 'center',
                    marginLeft: maxSM && 'auto'
                  }}
                >
                  <p>{user.name}</p>
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      signOut();

                      window.location.href =
                        `${process.env.REACT_APP_URL_CLIENT_VIEW}${process.env.REACT_APP_URL_BOTAO_VOLTAR}`;
                    }}
                    size="small"
                  >
                    Sair
                  </Button>
                </Stack>
              )}
            </Route> */}

            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                color: '#004e9a',
                marginTop: '40px',
                fontWeight: '500'
              }}
            >
              <Box>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleClickSearchButton}
                >
                  PESQUISAR
                </Button>
              </Box>

              <Paper
                elevation={2}
                style={{
                  marginTop: 16,
                  padding: '20px 16px 16px'
                }}
              >
                <Title style={{ marginBottom: 16 }}>
                  Solicitações Pendentes:
                </Title>

                <Stack
                  direction={maxMD ? 'column' : 'row'}
                  spacing={2}
                  style={{ marginBottom: 16 }}
                >
                  <ContentContainer>
                    <TableContainer sx={{ borderRadius: '4px' }}>
                      <Table size="small">
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell align="center">
                              Protocolo
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              Data da Solicitação
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              Tipo de Modal
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              Situação
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              Documentos Fiscais
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              Outros Documentos
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              Impressão de Etiquetas
                            </StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {rows?.length > 0 ? (
                            <>
                              {rows?.map((value) => {
                                let backgroundColor = '#c4f0b6';
                                let disableUpload = true;

                                if (
                                  String(value.pickupRequestStatus)
                                    .toLowerCase()
                                    .includes('aguardando validação')
                                ) {
                                  backgroundColor = '#f1e6a9';
                                } else if (
                                  String(value.pickupRequestStatus)
                                    .toLowerCase()
                                    .includes('aguardando envio')
                                ) {
                                  backgroundColor = '#f3cccc';
                                  disableUpload = false;
                                }

                                return (
                                  <StyledTableRow
                                    key={value.pickupId}
                                    style={{
                                      backgroundColor
                                    }}
                                  >
                                    <TableCell align="center">
                                      {value.protocol}
                                    </TableCell>
                                    <TableCell align="center">
                                      {format(
                                        new Date(value.createdAt),
                                        'dd/MM/yyyy'
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {value.tranportType}
                                    </TableCell>
                                    <TableCell align="center">
                                      {value.pickupRequestStatus}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Stack
                                        spacing={1}
                                        direction="row"
                                        justifyContent="center"
                                      >
                                        <IconButton
                                          type="button"
                                          onClick={async () => {
                                            setModalMessage('Pesquisando...');
                                            setProtocolToModal(value.protocol);
                                            setBranchParameterizationToModal(
                                              value.branchParameterizationId
                                            );

                                            if (value.invoiceList?.length > 0) {
                                              setInvoiceListToDownload(
                                                value.invoiceList
                                              );
                                            }

                                            openDownloadInvoicesModal();
                                          }}
                                        >
                                          <MdDownload
                                            style={{ fontSize: '20px' }}
                                            title="Download de Documentos Fiscais"
                                          />
                                        </IconButton>
                                        <IconButton
                                          type="button"
                                          disabled={
                                            value.pickupRequestStatus !==
                                            'Pré-coleta (Aguardando envio da NF)'
                                          }
                                          // disabled={disableUpload}
                                          // style={{
                                          //   cursor: disableUpload
                                          //     ? 'not-allowed'
                                          //     : 'pointer'
                                          // }}
                                          onClick={async () => {
                                            openLoadingModal();

                                            try {
                                              const findPickupResponse =
                                                await findPickupById({
                                                  pickupId: value.pickupId
                                                });

                                              if (
                                                findPickupResponse?.status ===
                                                200
                                              ) {
                                                const { pickup } =
                                                  findPickupResponse.data.data;

                                                if (
                                                  (pickup.shipmentList[0]
                                                    .taxInformationResponse
                                                    .taxInformationType !==
                                                    'Declaração' &&
                                                    pickup?.taxQuantity > 0 &&
                                                    pickup?.taxQuantity -
                                                      pickup?.taxInvoiceCount <=
                                                      0) ||
                                                  (pickup?.shipmentList[0]
                                                    ?.taxInformationResponse
                                                    ?.taxInformationType ===
                                                    'Declaração' &&
                                                    pickup?.taxQuantity > 0 &&
                                                    pickup?.taxQuantity -
                                                      pickup?.shipmentList[0]?.taxInformationResponse?.taxDocumentList?.filter(
                                                        ({
                                                          taxDocumentTypeId,
                                                          storageUrl
                                                        }) =>
                                                          taxDocumentTypeId ===
                                                            3 && !!storageUrl
                                                      )?.length <=
                                                      0)
                                                ) {
                                                  toast(
                                                    <Toast
                                                      title="Atenção!"
                                                      content="Limite de notas fiscais atingido."
                                                      type="error"
                                                    />,
                                                    options
                                                  );

                                                  closeModal();
                                                } else {
                                                  setProtocolToModal(
                                                    value.protocol
                                                  );
                                                  setPickupIdToModal(
                                                    value.pickupId
                                                  );
                                                  setBranchParameterizationToModal(
                                                    value.branchParameterizationId
                                                  );

                                                  if (pickup?.taxQuantity > 0) {
                                                    if (
                                                      pickup?.shipmentList[0]
                                                        ?.taxInformationResponse
                                                        ?.taxInformationType ===
                                                      'Declaração'
                                                    ) {
                                                      setLimitToUploadInvoice(
                                                        pickup?.taxQuantity -
                                                          pickup?.shipmentList[0]?.taxInformationResponse?.taxDocumentList?.filter(
                                                            ({
                                                              taxDocumentTypeId,
                                                              storageUrl
                                                            }) =>
                                                              taxDocumentTypeId ===
                                                                3 &&
                                                              !!storageUrl
                                                          )?.length
                                                      );
                                                    } else {
                                                      setLimitToUploadInvoice(
                                                        pickup?.taxQuantity -
                                                          pickup?.taxInvoiceCount
                                                      );
                                                    }
                                                  } else {
                                                    setLimitToUploadInvoice(
                                                      999
                                                    );
                                                  }

                                                  if (
                                                    pickup?.shipmentList[0]
                                                      ?.taxInformationResponse
                                                      ?.taxInformationType ===
                                                    'Declaração'
                                                  ) {
                                                    setIsPickupWithDeclaration(
                                                      true
                                                    );
                                                    openUploadDocumentsModal();
                                                  } else {
                                                    setIsPickupWithDeclaration(
                                                      false
                                                    );
                                                    openUploadInvoicesModal();
                                                  }
                                                }
                                              } else {
                                                throw new Error();
                                              }
                                            } catch (error) {
                                              // eslint-disable-next-line no-console
                                              console.log(error);

                                              closeModal();

                                              toast(
                                                <Toast
                                                  title="Atenção!"
                                                  content="Falha ao buscar detalhes da coleta."
                                                  type="error"
                                                />,
                                                options
                                              );
                                            }
                                          }}
                                        >
                                          <MdUpload
                                            style={{ fontSize: '20px' }}
                                            title="Upload de Documentos Fiscais"
                                          />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Stack
                                        spacing={1}
                                        direction="row"
                                        justifyContent="center"
                                      >
                                        <IconButton
                                          type="button"
                                          onClick={async () => {
                                            setModalMessage('Pesquisando...');
                                            setProtocolToModal(value.protocol);
                                            setBranchParameterizationToModal(
                                              value.branchParameterizationId
                                            );

                                            if (
                                              value.documentList?.length > 0
                                            ) {
                                              setDocumentListToDownload(
                                                value.documentList
                                              );
                                            }

                                            openDownloadDocumentsModal();
                                          }}
                                        >
                                          <MdDownload
                                            style={{ fontSize: '20px' }}
                                            title="Download de Outros Documentos"
                                          />
                                        </IconButton>
                                        <IconButton
                                          type="button"
                                          disabled={
                                            value.pickupRequestStatus !==
                                              'Pré-coleta (Aguardando validação da NF)' &&
                                            value.pickupRequestStatus !==
                                              'Pré-coleta (Aguardando envio da NF)'
                                          }
                                          onClick={async () => {
                                            openLoadingModal();

                                            try {
                                              const findPickupResponse =
                                                await findPickupById({
                                                  pickupId: value.pickupId
                                                });

                                              if (
                                                findPickupResponse?.status ===
                                                200
                                              ) {
                                                const { pickup } =
                                                  findPickupResponse.data.data;

                                                if (
                                                  pickup?.shipmentList[0]
                                                    ?.taxInformationResponse
                                                    ?.taxInformationType ===
                                                  'Declaração'
                                                ) {
                                                  setLimitToUploadInvoice(
                                                    pickup?.taxQuantity -
                                                      pickup?.shipmentList[0]?.taxInformationResponse?.taxDocumentList?.filter(
                                                        ({
                                                          taxDocumentTypeId,
                                                          storageUrl
                                                        }) =>
                                                          taxDocumentTypeId ===
                                                            3 && !!storageUrl
                                                      )?.length
                                                  );
                                                  setIsPickupWithDeclaration(
                                                    true
                                                  );
                                                } else {
                                                  setLimitToUploadInvoice(0);
                                                  setIsPickupWithDeclaration(
                                                    false
                                                  );
                                                }

                                                setProtocolToModal(
                                                  value.protocol
                                                );
                                                setPickupIdToModal(
                                                  value.pickupId
                                                );
                                                setBranchParameterizationToModal(
                                                  value.branchParameterizationId
                                                );
                                                setIsPickupWithDeclaration(
                                                  true
                                                );

                                                openUploadDocumentsModal();
                                              } else {
                                                throw new Error();
                                              }
                                            } catch (error) {
                                              // eslint-disable-next-line no-console
                                              console.log(error);

                                              closeModal();

                                              toast(
                                                <Toast
                                                  title="Atenção!"
                                                  content="Falha ao buscar detalhes da coleta."
                                                  type="error"
                                                />,
                                                options
                                              );
                                            }
                                          }}
                                        >
                                          <MdUpload
                                            style={{ fontSize: '20px' }}
                                            title="Upload de Outros Documentos"
                                          />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Stack justifyContent="center">
                                        <IconButton
                                          type="button"
                                          disabled={
                                            !(
                                              value.pickupRequestStatus?.toLowerCase() ===
                                                'programada (aguardando impressão da etiqueta)' ||
                                              value.pickupRequestStatus?.toLowerCase() ===
                                                'programada'
                                            )
                                          }
                                          onClick={async () => {
                                            setModalMessage('Pesquisando...');
                                            setProtocolToModal(value.protocol);
                                            setPickupIdToModal(value.pickupId);
                                            setBranchParameterizationToModal(
                                              value.branchParameterizationId
                                            );

                                            if (value.invoiceList?.length > 0) {
                                              const invoiceListFiltered =
                                                value?.invoiceList?.filter(
                                                  (invoice) =>
                                                    invoice.typeDocument ===
                                                    'XML'
                                                );

                                              setInvoiceListToDownload(
                                                invoiceListFiltered
                                              );
                                            } else {
                                              setInvoiceListToDownload([]);
                                            }

                                            openPrintTagModal();
                                          }}
                                        >
                                          <MdDownload
                                            style={{
                                              fontSize: '20px'
                                            }}
                                            title="Impressão de Etiquetas"
                                          />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  </StyledTableRow>
                                );
                              })}
                            </>
                          ) : (
                            <StyledTableRow>
                              {openedModal === 'modalLoading' ? (
                                <TableCell align="center" colSpan={7}>
                                  Carregando...
                                </TableCell>
                              ) : (
                                <TableCell align="center" colSpan={7}>
                                  Sem resultado
                                </TableCell>
                              )}
                            </StyledTableRow>
                          )}
                        </TableBody>
                        {rows.length > 0 && (
                          <TableFooter>
                            <TableRow>
                              <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                count={totalRecords}
                                rowsPerPage={rowsPerPage}
                                page={page - 1}
                                labelDisplayedRows={() => {
                                  // eslint-disable-next-line prettier/prettier
                                  return `Página ${page} de ${totalPages}`;
                                }}
                                labelRowsPerPage="Itens por página:"
                                onPageChange={(event, newPage) => {
                                  // !isSearching &&
                                  // setIsSubmittingPagination(true);
                                  handleChangePage(event, newPage);
                                }}
                                onRowsPerPageChange={(event) => {
                                  // !isSearching &&
                                  handleChangeRowsPerPage(event);
                                }}
                                showFirstButton={totalPages > 2}
                                showLastButton={totalPages > 2}
                              />
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </TableContainer>
                  </ContentContainer>
                </Stack>
              </Paper>

              <Paper
                elevation={2}
                style={{
                  marginTop: 16,
                  padding: '20px 16px 16px'
                }}
              >
                <Title style={{ marginBottom: 16 }}>Legenda</Title>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    spacing={2}
                    style={{ alignItems: 'stretch' }}
                  >
                    <div
                      style={{
                        backgroundColor: '#f3cccc', // #f38b8b
                        border: '1px solid #000',
                        width: '64px',
                        minWidth: '64px'
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      Solicitação aguardando envio de documentação
                    </span>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={2}
                    style={{ alignItems: 'stretch' }}
                  >
                    <div
                      style={{
                        backgroundColor: '#f1e6a9', // #f0dc73
                        border: '1px solid #000',
                        width: '64px',
                        minWidth: '64px'
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      Solicitação em análise interna pela Braspress
                    </span>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={2}
                    style={{ alignItems: 'stretch' }}
                  >
                    <div
                      style={{
                        backgroundColor: '#c4f0b6', // #69b94f
                        border: '1px solid #000',
                        width: '64px',
                        minWidth: '64px'
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      Solicitação liberada para impressão de etiqueta
                    </span>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Box>
        </Content>
      </Container>
      {openedModal === 'modalDownloadDocuments' && <ModalDownloadDocuments />}
      {openedModal === 'modalDownloadInvoices' && <ModalDownloadInvoices />}
      {openedModal === 'modalUploadDocuments' && <ModalUploadDocuments />}
      {openedModal === 'modalUploadInvoices' && <ModalUploadInvoices />}
      {openedModal === 'modalPrintTag' && <ModalPrintTag />}
      {openedModal === 'modalLoading' && <ModalLoading />}
    </MyCollectionsContext.Provider>
  );
};

export default MyCollections;
